"use client";

import Link from "next/link";
import { Plus } from "lucide-react";
import type { ReactNode, RefCallback } from "react";

import { Rosette, SecGlyph } from "../ornament";
import { Spinner } from "../ui";
import "./desk.css";

export type TallyCell = { key: string; label: string; value: string; tone?: "accent" | "live" | "up" | "gold" | "down" | "muted"; note?: string };

/**
 * THE OWNER'S DESK — «میز کار». What the owner keeps in one drawer (their
 * auctions, bids, galleries, drops) opens on a ledger cover: the drawer's
 * name over a faint ledger ground, its figures set in a ruled row like the
 * first page of an account book, and the one way to add to it.
 *
 * In a profile tab the cover carries the title; on its own route the route
 * hero already does, so the cover drops it and keeps the figures and the action.
 */
export function DeskCover({ title, lead, tally, create, children }: {
  title?: string;
  lead?: string;
  tally: TallyCell[];
  create?: { href: string; label: string };
  children?: ReactNode;
}) {
  const createLink = create ? <Link href={create.href} className="v2-btn dk-create v2-focus"><Plus aria-hidden />{create.label}</Link> : null;
  // With no title (the route hero has it) the action closes the tally row instead of standing alone on a strip.
  const actInTally = !title && tally.length > 0;
  return (
    <section className="dk-cover" aria-label={title}>
      {title || (create && !actInTally) ? (
        <header className="dk-cover-head">
          {title ? (
            <div className="dk-cover-copy">
              <h2><SecGlyph className="dk-glyph" />{title}</h2>
              {lead ? <p>{lead}</p> : null}
            </div>
          ) : null}
          {createLink}
        </header>
      ) : null}
      {tally.length > 0 ? (
        <div className="dk-tally" data-n={tally.length} data-act={actInTally && create ? true : undefined}>
          <dl>
            {tally.map((c) => (
              <div key={c.key} className="dk-cell" data-tone={c.tone}>
                <dt>{c.label}</dt>
                <dd><bdi>{c.value}</bdi></dd>
                {c.note ? <small>{c.note}</small> : null}
              </div>
            ))}
          </dl>
          {actInTally && createLink ? <div className="dk-act">{createLink}</div> : null}
        </div>
      ) : null}
      {children}
    </section>
  );
}

/** The filters under the cover — the catalogue quick-bar's controls, wrapped, with a reset at the end. */
export function DeskBar({ label, children, onReset, resetLabel, canReset }: { label: string; children: ReactNode; onReset?: () => void; resetLabel?: string; canReset?: boolean }) {
  return (
    <section className="dk-bar" aria-label={label}>
      {children}
      {onReset ? <button type="button" className="dk-reset v2-focus" onClick={onReset} disabled={!canReset}>{resetLabel}</button> : null}
    </section>
  );
}

/** A choice of views over one list, each with its count when the count is known. */
export function DeskSeg<T extends string>({ value, options, onChange, label }: { value: T; options: { key: T; label: string; count?: string | null }[]; onChange: (v: T) => void; label: string }) {
  return (
    <div className="dk-seg" role="group" aria-label={label}>
      {options.map((o) => (
        <button key={o.key} type="button" className="v2-focus" aria-pressed={value === o.key} onClick={(e) => { onChange(o.key); e.currentTarget.scrollIntoView({ block: "nearest", inline: "nearest" }); }}>
          {o.label}{o.count ? <b>{o.count}</b> : null}
        </button>
      ))}
    </div>
  );
}

/** Twelve first, then more as the reader scrolls — the button stays for those who don't. */
export function DeskMore({ show, busy, onMore, sentinel, label }: { show: boolean; busy: boolean; onMore: () => void; sentinel: RefCallback<HTMLElement> | ((node: HTMLElement | null) => void); label: string }) {
  if (!show) return null;
  return (
    <div className="dk-more" ref={sentinel}>
      <button type="button" className="v2-btn v2-btn--ghost v2-focus" onClick={onMore} disabled={busy} aria-busy={busy || undefined}>
        {busy ? <Spinner /> : null}{label}
      </button>
    </div>
  );
}

/**
 * An empty drawer — a blank ledger page: faint ruled lines and a gilt margin
 * fading towards the middle, the ده‌پر rosette with the drawer's own mark in
 * a gilt diamond, then what is (not) here and what to do about it. `tone`
 * says why it is empty: nothing yet, nothing for these filters, or a failure
 * (announced).
 */
export function DeskEmpty({ icon, title, body, actions, tone = "empty" }: { icon: ReactNode; title: string; body?: string; actions?: ReactNode; tone?: "empty" | "filter" | "error" }) {
  return (
    <div className="dk-empty" data-tone={tone} role={tone === "error" ? "alert" : undefined}>
      <span className="dk-empty-mark" aria-hidden>
        <Rosette className="dk-empty-rosette" />
        <span className="dk-empty-icon">{icon}</span>
      </span>
      <h3>{title}</h3>
      {body ? <p>{body}</p> : null}
      {actions ? <div className="dk-empty-acts">{actions}</div> : null}
    </div>
  );
}

/** A status mark: a dot and a word, its colour from the tone. */
export function Seal({ tone, children }: { tone: "accent" | "live" | "up" | "gold" | "down" | "muted"; children: ReactNode }) {
  return <span className="dk-seal" data-tone={tone}>{children}</span>;
}
